import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { StatusFilter } from './orders.selectors';

const DATE_FORMAT = 'YYYY-MM-DD';
const today = () => moment().format(DATE_FORMAT);

/**
 * State for the orders screen.
 *
 * Deliberately separate from `orderListSlice`, which the POS cart and the
 * dashboard still own: those two have a different lifecycle (they push a
 * freshly placed order in and cache aggressively) and mixing the two is what
 * made the old screen refetch at random.
 *
 * Only `range` costs a round trip. Status, waiter and search all narrow the
 * loaded set in memory, so changing a chip is instant.
 */
const initialState = {
  orders: [],
  /** 'idle' | 'loading' | 'ready' | 'error' */
  status: 'idle',
  error: null,

  range: { start: today(), end: today() },

  statusFilter: StatusFilter.ALL,
  staffId: null,
  search: '',

  selectedOrderId: null,

  /** Id of the order whose status is being written, so one row can spin alone. */
  busyOrderId: null,
  /** A failed row action — shown as a strip, never as a whole-screen error. */
  actionError: null,
  /** Id of the order the cancel dialog is open for. */
  cancelingOrderId: null,
  cancelError: null,
  isCanceling: false,
};

const ordersScreenSlice = createSlice({
  name: 'ordersScreen',
  initialState,
  reducers: {
    ordersRequested(state) {
      state.status = 'loading';
      state.error = null;
    },
    ordersReceived(state, action) {
      state.orders = action.payload;
      state.status = 'ready';
      state.error = null;

      // Keep the panel honest: a selected order that fell out of the new range
      // must not stay open showing stale figures.
      const stillThere = action.payload.some(
        (o) => String(o._id || o.id) === state.selectedOrderId
      );
      if (!stillThere) state.selectedOrderId = null;
    },
    ordersFailed(state, action) {
      state.status = 'error';
      state.error = action.payload;
    },

    /** Replaces one row in place after a status change — no refetch. */
    orderUpdated(state, action) {
      const updated = action.payload;
      const id = String(updated._id || updated.id);
      state.orders = state.orders.map((o) =>
        String(o._id || o.id) === id ? { ...o, ...updated } : o
      );
    },
    orderRemoved(state, action) {
      const id = String(action.payload);
      state.orders = state.orders.filter((o) => String(o._id || o.id) !== id);
      if (state.selectedOrderId === id) state.selectedOrderId = null;
    },

    rangeChanged(state, action) {
      state.range = action.payload;
    },
    statusFilterChanged(state, action) {
      state.statusFilter = action.payload;
    },
    staffFilterChanged(state, action) {
      state.staffId = action.payload;
    },
    searchChanged(state, action) {
      state.search = action.payload;
    },
    filtersCleared(state) {
      state.statusFilter = StatusFilter.ALL;
      state.staffId = null;
      state.search = '';
    },

    orderSelected(state, action) {
      state.selectedOrderId = action.payload;
    },

    statusWriteStarted(state, action) {
      state.busyOrderId = action.payload;
    },
    statusWriteFinished(state) {
      state.busyOrderId = null;
    },
    actionFailed(state, action) {
      state.actionError = action.payload;
    },
    actionErrorCleared(state) {
      state.actionError = null;
    },

    cancelDialogOpened(state, action) {
      state.cancelingOrderId = action.payload;
      state.cancelError = null;
      state.isCanceling = false;
    },
    cancelDialogClosed(state) {
      state.cancelingOrderId = null;
      state.cancelError = null;
      state.isCanceling = false;
    },
    cancelStarted(state) {
      state.isCanceling = true;
      state.cancelError = null;
    },
    cancelFailed(state, action) {
      state.isCanceling = false;
      state.cancelError = action.payload;
    },

    /** Called on logout so the next user never sees the previous one's orders. */
    ordersScreenReset() {
      return { ...initialState, range: { start: today(), end: today() } };
    },
  },
});

export const {
  ordersRequested,
  ordersReceived,
  ordersFailed,
  orderUpdated,
  orderRemoved,
  rangeChanged,
  statusFilterChanged,
  staffFilterChanged,
  searchChanged,
  filtersCleared,
  orderSelected,
  statusWriteStarted,
  statusWriteFinished,
  actionFailed,
  actionErrorCleared,
  cancelDialogOpened,
  cancelDialogClosed,
  cancelStarted,
  cancelFailed,
  ordersScreenReset,
} = ordersScreenSlice.actions;

export default ordersScreenSlice.reducer;
